import React, { useRef, useContext, useMemo, useState, useEffect } from "react";
import { Box, Chip, Grid, TextField, Typography } from "@mui/material";
import Option, { OPTION_GRID_TILE_SX } from "./Option";
import OptionGridCell from "./OptionGridCell";
import {
  buildPhotoVariantValue,
  getItemBasePrice,
  getVariantOptionSurchargeForDisplay,
  sortVariantOptions,
} from "../../common/util";
import PhotoVariantTiles from "./PhotoVariantTiles";
import VariantSelectionStatusBar from "./VariantSelectionStatusBar";
import VariantSelectionCategory from "./VariantSelectionCategory";
import FlavorSelectionTouch from "./FlavorSelectionTouch";
import AddCustomOption from "./AddCustomOption";
import currency from "currency.js";
import { UserContext } from "../../context/UserContext";

export default function VariantSelection(props) {
  const { user } = useContext(UserContext);
  const { options, variant } = props;

  // Refs for each category
  const categoryRefs = useRef({});
  const [sizeShapeFilter, setSizeShapeFilter] = useState("All");

  const sizeOptions = useMemo(() => {
    const sizeField = options?.find?.(([k]) => k === "size")?.[1];
    return sizeField?.field?.options || [];
  }, [options]);

  const sizeShapes = useMemo(() => {
    const shapes = new Set();
    sizeOptions.forEach((o) => {
      if (o.shape) shapes.add(o.shape);
    });
    return Array.from(shapes).sort((a, b) => a.localeCompare(b));
  }, [sizeOptions]);

  const sizeShapeKey = sizeShapes.join("|");
  useEffect(() => {
    setSizeShapeFilter("All");
  }, [sizeShapeKey]);

  const getOptionName = (fieldKey, option) => {
    let str = "";
    if (fieldKey === "size" && option.size) {
      str = option.size;
    } else {
      str = option.name;
    }

    return str;
  };

  const getOptionDescription = (fieldKey, option) => {
    if (fieldKey === "size" && option.servings) {
      let arr = [];
      if (option.shape) {
        arr.push(option.shape);
      }
      if (option.layer) {
        arr.push(`${option.layer} Layers`);
      }
      if (option.servings) {
        arr.push(`${option.servings} Servings`);
      }
      return arr.join(" • ");
    } else {
      return option.description;
    }
  };

  const sortVariants = ([k1, v1], [k2, v2]) => {
    return v2.rank - v1.rank || k2.localeCompare(k1);
  };

  const isSelected = (field, option, value) => {
    if (+field?.max > 1) {
      if (value && Array.isArray(value)) {
        return value.some((o) => o.name === option.name);
      }
    } else {
      return option.name === value?.name;
    }

    return false;
  };

  const basePrice = props.basePrice || getItemBasePrice(variant);

  return (
    <Box>
      {props.showStatusBar && (
        <VariantSelectionStatusBar
          variant={variant || ""}
          options={options}
          onDelete={(key) => {
            props.onChange(key, "");
          }}
        />
      )}
      {options.sort(sortVariants).map(([key, value]) => {
        if (value.field.type === "text") {
          return (
            <Box my={3}>
              <TextField
                id={key}
                value={variant?.[key]?.name || ""}
                required={value.required}
                label={value.name}
                name={key}
                variant="outlined"
                //inputProps={{ maxLength: value.field?.max }}
                fullWidth
                multiline={value.field?.max > 40}
                rows={Math.floor(value.field?.max / 40)}
                onChange={(e) => {
                  props.onChange({
                    ...variant,
                    [key]: { name: e.target.value, rank: value.rank || 1 },
                  });
                }}
                helperText={value.description}
              />
            </Box>
          );
        } else if (value.field.type === "photo") {
          const photos = variant?.[key]?.value || [];
          const photoSurcharge = getVariantOptionSurchargeForDisplay(value, basePrice);
          // saas-app product page style: "Photo (+15.00)*"
          const photoSurchargeLabel = photoSurcharge.includes("%")
            ? photoSurcharge
            : currency(photoSurcharge).toString();
          const applyPhotos = (arr) => {
            const next = { ...variant };
            const photoVal = buildPhotoVariantValue(value, arr);
            if (photoVal) {
              next[key] = photoVal;
            } else {
              delete next[key];
            }
            props.onChange(next);
          };

          return (
            <VariantSelectionCategory
              key={key}
              title={value.name}
              field={value.field}
              cat={key}
              basePrice={basePrice}
              variant={variant?.[key]}
              complete={photos.length > 0}
              placeholderLabel={`${value.name}${photoSurcharge ? ` (+${photoSurchargeLabel})` : ""}${
                value.required ? "*" : ""
              }`}
              alwaysOpen={true}
              dontCollapseOnSelect={true}
            >
              <Box py={2}>
                <PhotoVariantTiles
                  pics={photos}
                  max={value.field?.max || 1}
                  folder="item"
                  onChange={applyPhotos}
                />
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1.5 }}>
                  {value.description ||
                    "For best results, use high-resolution photos in JPEG or PNG format. We will crop the image to achieve the best possible fit, but we will not alter the design of the uploaded photos."}
                </Typography>
              </Box>
            </VariantSelectionCategory>
          );
        } else if (
          (key === "flavor" || key === "filling") &&
          variant?.size?.value?.settings?.field?.flavor
        ) {
          return (
            <VariantSelectionCategory
              ref={(el) => {
                if (el) categoryRefs.current[key] = el;
              }}
              title={value.name}
              field={value.field}
              cat={key}
              basePrice={basePrice}
              variant={variant?.[key]}
              alwaysOpen={true}
              dontCollapseOnSelect={options.length == 1}
            >
              <Box py={2}>
                <FlavorSelectionTouch
                  basePrice={basePrice}
                  fieldKey={key}
                  variant={variant}
                  fields={Object.fromEntries(options)}
                  onChange={(flavor, filling) => {
                    let o = { ...variant };
                    if (flavor) {
                      o.flavor = flavor;
                    }
                    if (filling) {
                      o.filling = filling;
                    }
                    props.onChange(o);
                    // Scroll to top of this component
                    if (categoryRefs.current[key]) {
                      categoryRefs.current[key].scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }}
                />
              </Box>
            </VariantSelectionCategory>
          );
        } else if (value.field.type === "select") {
          const isMultiSelect = +value.field?.max > 1;
          const showSizeShapeFilter = key === "size" && !isMultiSelect && sizeShapes.length >= 2;
          const selectOptions = (
            showSizeShapeFilter && sizeShapeFilter !== "All"
              ? value.field.options.filter((o) => o.shape === sizeShapeFilter)
              : value.field.options
          ).sort((o1, o2) => sortVariantOptions(key, o1, o2));

          return (
            <VariantSelectionCategory
              title={value.name}
              basePrice={basePrice}
              field={value.field}
              cat={key}
              variant={variant?.[key]}
              alwaysOpen={true}
              dontCollapseOnSelect={options.length == 1}
            >
              <Box py={2}>
                {showSizeShapeFilter && (
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {["All", ...sizeShapes].map((shape) => (
                      <Chip
                        key={shape}
                        label={shape}
                        clickable
                        color={sizeShapeFilter === shape ? "primary" : "default"}
                        variant={sizeShapeFilter === shape ? "filled" : "outlined"}
                        onClick={() => setSizeShapeFilter(shape)}
                        sx={{ fontSize: "1rem", height: 40, px: 0.5 }}
                      />
                    ))}
                  </Box>
                )}
                <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
                  {selectOptions.map((option, idx) => {
                      let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);
                      // Size base price: show with + like other option upcharges
                      const sizeBasePrice =
                        key === "size" && +option.pricing?.amount > 0
                          ? `+${currency(option.pricing.amount).format()}`
                          : "";
                      const priceLabel = sizeBasePrice || (surcharge ? `+${surcharge}` : "");

                      const selected = isSelected(value.field, option, variant?.[key]?.value);

                      return (
                        <OptionGridCell gridKey={`${key}-${option.name ?? "opt"}-${idx}`}>
                          <Option
                            price={priceLabel}
                            title={getOptionName(key, option)}
                            description={getOptionDescription(key, option)}
                            sx={OPTION_GRID_TILE_SX}
                            onClick={(selected) => {
                                if (+value.field?.max > 1) {
                                  let arr = variant?.[key]?.value || [];
                                  if (selected) {
                                    arr.push(option);
                                  } else {
                                    const index = arr.findIndex((o) => o.name === option.name);
                                    arr.splice(index, 1);
                                  }
                                  const name = arr.map((o) => o.name).join(", ");
                                  props.onChange({
                                    ...variant,
                                    [key]: { name, rank: value.rank || 1, value: arr },
                                  });
                                } else {
                                  let tmp = { ...variant };
                                  if (selected) {
                                    const { override, ...opt } = option;
                                    tmp[key] = { ...opt, rank: value.rank || 1, value: opt };
                                  } else {
                                    delete tmp[key];
                                  }
                                  if (
                                    key === "size" &&
                                    (user.acctId === "ZOsiHlROlfEcAO7PGGCK" ||
                                      user.acctId === "99i69dd8bwTqlxzVLZUx")
                                  ) {
                                    Object.keys(tmp).forEach((k) => {
                                      if (k !== "size") {
                                        delete tmp[k];
                                      }
                                    });
                                  }
                                  props.onChange(tmp);
                                }
                              }}
                              selected={selected}
                            />
                        </OptionGridCell>
                      );
                    })}
                  {!isMultiSelect && (
                    <OptionGridCell gridKey={`${key}-custom`}>
                      {variant?.[key]?.name &&
                      !value.field.options.find((o) => o.name === variant?.[key]?.name) ? (
                        <Option
                          price={
                            +variant?.[key]?.pricing?.amount > 0
                              ? currency(variant?.[key]?.pricing?.amount).format()
                              : ""
                          }
                          title={variant?.[key]?.name}
                          sx={OPTION_GRID_TILE_SX}
                          onClick={(selected) => {
                            let tmp = { ...variant };
                            if (selected) {
                              tmp[key] = variant?.[key]?.value;
                            } else {
                              delete tmp[key];
                            }

                            props.onChange(tmp);
                          }}
                          selected={true}
                        />
                      ) : (
                        <AddCustomOption
                          variantKey={key}
                          label={`Add Custom ${value.name}`}
                          onChange={(o) => {
                            let tmp = { ...variant };
                            if (o) {
                              tmp[key] = { ...o, value: o };
                            } else {
                              delete tmp[key];
                            }
                            props.onChange(tmp);
                          }}
                        >
                          <Option
                            title={`Custom`}
                            description={`Enter ${value.name}`}
                            sx={OPTION_GRID_TILE_SX}
                            onClick={(selected) => {}}
                          />
                        </AddCustomOption>
                      )}
                    </OptionGridCell>
                  )}
                </Grid>
              </Box>
            </VariantSelectionCategory>
          );
        }
      })}
    </Box>
  );
}
